import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CheckCheck, Clock, Copy, CornerUpLeft, Download, FileText, Link2, MoreHorizontal, Pencil, RotateCcw, SmilePlus, Trash2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/ui/DropdownMenu';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import type { ChatMessage, SharePayload } from '../model/types';
import { AuthImage, downloadAttachment } from './AuthImage';
import { fileSize, linkify, QUICK_REACTIONS, timeOf } from './chatFormat';

export interface MessageBubbleProps {
  message: ChatMessage;
  mine: boolean;
  /** Grupta başkasının mesajında gönderen adı (art arda gelenlerde yalnız ilkinde). */
  showSender: boolean;
  isGroup: boolean;
  myUserId: string;
  onReply: (m: ChatMessage) => void;
  onEdit: (m: ChatMessage) => void;
  onDelete: (m: ChatMessage) => void;
  onReact: (m: ChatMessage, emoji: string) => void;
  onRetry: (m: ChatMessage) => void;
  onOpenImage: (url: string, name: string) => void;
  onOpenShare: (p: SharePayload) => void;
  onJumpTo: (messageId: string) => void;
}

function parseShare(payload: string | null): SharePayload | null {
  if (!payload) return null;
  try {
    const p = JSON.parse(payload) as SharePayload;
    return p && p.url ? p : null;
  } catch {
    return null;
  }
}

export const MessageBubble = memo(function MessageBubble(p: MessageBubbleProps) {
  const { t } = useTranslation();
  const m = p.message;
  const [reactOpen, setReactOpen] = useState(false);
  const images = m.attachments.filter((a) => a.contentType?.startsWith('image/') && a.contentType !== 'image/svg+xml');
  const files = m.attachments.filter((a) => !images.includes(a));
  const share = m.type === 'share' ? parseShare(m.payload) : null;
  // Yalnız ekten oluşan mesajda sunucu içeriğe "📎 dosya" yazar; balonda tekrar göstermeyiz.
  const autoContent = m.attachments.length > 0 && m.content === m.attachments.map((a) => '📎 ' + a.fileName).join(', ');
  const text = m.deleted ? '' : autoContent ? '' : m.content;
  const read = p.isGroup ? m.recipientCount > 0 && m.readCount >= m.recipientCount : !!m.readAt;

  return (
    <div data-message-id={m.messageId} className={cn('group/msg flex w-full px-3', p.mine ? 'justify-end' : 'justify-start')}>
      <div className={cn('flex max-w-[85%] flex-col', p.mine ? 'items-end' : 'items-start')}>
        {p.showSender && !p.mine ? <span className="text-brand-600 mb-0.5 px-1 text-[11px] font-semibold">{m.senderName}</span> : null}
        <div className="flex items-end gap-1">
          {/* İşlemler: balonun yanında, üzerine gelince. */}
          {!m.deleted && !m.pending ? (
            <div className={cn('flex shrink-0 items-center opacity-0 transition-opacity group-hover/msg:opacity-100 focus-within:opacity-100', p.mine ? 'order-first' : 'order-last')}>
              <Popover open={reactOpen} onOpenChange={setReactOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="text-fg-subtle hover:bg-bg-hover hover:text-fg rounded p-1" aria-label={t('chat.react')}>
                    <SmilePlus className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" className="flex gap-0.5 p-1">
                  {QUICK_REACTIONS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      className="hover:bg-bg-hover rounded p-1 text-lg leading-none"
                      onClick={() => {
                        p.onReact(m, e);
                        setReactOpen(false);
                      }}
                    >
                      {e}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
              <button type="button" className="text-fg-subtle hover:bg-bg-hover hover:text-fg rounded p-1" aria-label={t('chat.reply')} onClick={() => p.onReply(m)}>
                <CornerUpLeft className="h-4 w-4" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="text-fg-subtle hover:bg-bg-hover hover:text-fg rounded p-1" aria-label={t('chat.more')}>
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={p.mine ? 'end' : 'start'}>
                  {text ? (
                    <DropdownMenuItem onSelect={() => void navigator.clipboard.writeText(m.content)}>
                      <Copy className="h-4 w-4" /> {t('chat.copy')}
                    </DropdownMenuItem>
                  ) : null}
                  {p.mine && m.type === 'text' ? (
                    <DropdownMenuItem onSelect={() => p.onEdit(m)}>
                      <Pencil className="h-4 w-4" /> {t('chat.edit')}
                    </DropdownMenuItem>
                  ) : null}
                  {p.mine ? (
                    <DropdownMenuItem onSelect={() => p.onDelete(m)}>
                      <Trash2 className="text-danger h-4 w-4" /> {t('chat.delete')}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}

          <div
            className={cn(
              'relative min-w-[4.5rem] overflow-hidden rounded-2xl text-sm shadow-sm',
              p.mine ? 'bg-brand-600 rounded-br-md text-white' : 'bg-bg-muted text-fg rounded-bl-md',
              m.deleted && 'bg-transparent text-fg-subtle border-border border italic shadow-none',
              m.pending === 'failed' && 'ring-danger ring-2',
            )}
          >
            {m.replyTo ? (
              <button
                type="button"
                onClick={() => p.onJumpTo(m.replyTo!.messageId)}
                className={cn('m-1.5 mb-0 block w-[calc(100%-0.75rem)] rounded-lg border-l-4 px-2 py-1 text-left text-xs', p.mine ? 'border-white/70 bg-white/15' : 'border-brand-500 bg-bg-elevated')}
              >
                <span className="block font-semibold">{m.replyTo.senderName}</span>
                <span className="line-clamp-2 opacity-80">{m.replyTo.preview || '…'}</span>
              </button>
            ) : null}

            {images.length ? (
              <div className={cn('grid gap-0.5 p-0.5', images.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
                {images.map((a) => (
                  <AuthImage
                    key={a.attachmentId}
                    url={a.url}
                    alt={a.fileName}
                    onClick={() => p.onOpenImage(a.url, a.fileName)}
                    className={cn('rounded-xl', images.length > 1 ? 'h-32 w-full' : 'max-h-72 w-full min-w-48')}
                  />
                ))}
              </div>
            ) : null}

            {files.map((a) => (
              <button
                key={a.attachmentId}
                type="button"
                onClick={() => void downloadAttachment(a.url, a.fileName)}
                className={cn('m-1.5 flex w-[calc(100%-0.75rem)] min-w-52 items-center gap-2 rounded-xl px-2 py-1.5 text-left', p.mine ? 'bg-white/15 hover:bg-white/25' : 'bg-bg-elevated hover:bg-bg-hover')}
              >
                <FileText className="h-8 w-8 shrink-0 opacity-80" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{a.fileName}</span>
                  <span className="text-[11px] opacity-75">{fileSize(a.size)}</span>
                </span>
                <Download className="h-4 w-4 shrink-0 opacity-75" />
              </button>
            ))}

            {share ? (
              <button
                type="button"
                onClick={() => p.onOpenShare(share)}
                className={cn('m-1.5 flex w-[calc(100%-0.75rem)] min-w-56 items-start gap-2 rounded-xl px-2.5 py-2 text-left', p.mine ? 'bg-white/15 hover:bg-white/25' : 'bg-bg-elevated hover:bg-bg-hover border-border border')}
              >
                <Link2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{share.caption}</span>
                  {share.summary ? <span className="block text-xs opacity-80">{share.summary}</span> : null}
                  <span className="mt-0.5 block text-[11px] font-medium underline underline-offset-2 opacity-90">{t('chat.share.open')}</span>
                </span>
              </button>
            ) : null}

            {m.deleted ? (
              <p className="px-3 py-1.5">{t('chat.deleted')}</p>
            ) : text ? (
              <p className="px-3 pt-1.5 pb-0.5 break-words whitespace-pre-wrap">{linkify(text)}</p>
            ) : null}

            <div className={cn('flex items-center justify-end gap-1 px-2.5 pb-1 text-[10px]', p.mine ? 'text-white/75' : 'text-fg-subtle', !text && !m.deleted && 'pt-0.5')}>
              {m.editedAt && !m.deleted ? <span>{t('chat.edited')}</span> : null}
              <span className="tabular-nums">{timeOf(m.sentAt)}</span>
              {p.mine && !m.deleted ? (
                m.pending === 'sending' ? (
                  <Clock className="h-3 w-3" />
                ) : m.pending === 'failed' ? null : read ? (
                  <CheckCheck className="h-3.5 w-3.5 text-sky-300" aria-label={t('chat.read')} />
                ) : (
                  <Check className="h-3.5 w-3.5" aria-label={t('chat.sent')} />
                )
              ) : null}
            </div>
          </div>
        </div>

        {m.pending === 'failed' ? (
          <button type="button" onClick={() => p.onRetry(m)} className="text-danger mt-0.5 inline-flex items-center gap-1 text-[11px] hover:underline">
            <RotateCcw className="h-3 w-3" /> {t('chat.retry')}
          </button>
        ) : null}

        {m.reactions.length ? (
          <div className={cn('-mt-1 flex flex-wrap gap-1', p.mine ? 'justify-end pr-2' : 'pl-2')}>
            {m.reactions.map((r) => {
              const mineReacted = r.userIds.some((u) => u.toLowerCase() === p.myUserId.toLowerCase());
              return (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => p.onReact(m, r.emoji)}
                  className={cn(
                    'bg-bg-elevated border-border inline-flex items-center gap-0.5 rounded-full border px-1.5 text-xs shadow-sm',
                    mineReacted && 'border-brand-500 bg-brand-500/10',
                  )}
                >
                  <span>{r.emoji}</span>
                  {r.userIds.length > 1 ? <span className="tabular-nums">{r.userIds.length}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
});
